<script lang="ts" setup>
import { ref, computed, defineAsyncComponent } from "vue";
import { useMixins } from '@/plugins/mixins';
import { useLocalization } from '@/plugins/localization';
import { createPopper as createPopperInstance, Instance as Popper } from '@popperjs/core';
import { ParticipantModel, Seen } from '@/modules/logito/office/services/RegisterService';
import IdeoModal from "@/components/ideo/modal/IdeoModal.vue";
import { KeyValuePair } from "@/helpers/Interfaces";
import ParticipantsService from "../services/ParticipantsService";
import Loader from "@/components/common/Loader.vue";

// Use async component to break circular dependency
const ReadSharingModal = defineAsyncComponent(() => import("./modals/ReadSharingModal.vue"));

const emit = defineEmits(["delete"]);

const props = defineProps({
  "user": null,
  "index": { default: '' },
  "customClass": { default: '' },
  "tooltip": { type: Boolean, default: true },
  "canDelete": { type: Boolean,  },
  "added": { type: Boolean,  },
  "removed": { type: Boolean,  },
  "isHistoryMode": { type: Boolean,  },
  "licence": { default: null },
  "documentId": { default: null },
  "historyId": { default: null },
  "publicId": { default: null }
});

const { $filters } = useMixins();
const { $t } = useLocalization();

const popper = ref<Popper>(null);
const icon = ref<HTMLElement>(null);
const menu = ref<HTMLElement>(null);
const readSharingModal = ref<any>(null);
const readSharingData = ref<any[]>([]);

const isSomeoneHaveSeen = computed(() => props.user.isSeen);
const isUserWhoReadShare = computed(() => props.user.participantType && props.user.participantType.key == 'User' && props.user.readSharingData && props.user.readSharingData[0]?.readSharingDateUtc);
const title = ref<string>($t('[[[Udostępniono dla]]]'));
const loadingVisits = ref(false);
const loadingModalVisits = ref(false);
const currentVisits = ref<Seen[]>(null);

const deleteItem = (user: ParticipantModel): void =>
{
    emit('delete', user);
};

const messageSeenBy = (seenBy: Seen): string =>
{
    const user = seenBy.seenBy;
    const impersonator = seenBy.seenByImpersonator;
    const date = $filters.datetime(seenBy.seenDateUtc);

    return `${user.value} ${impersonator ? `(${impersonator.value})` : ''} ${$t('[[[zobaczył/a]]]')} ${date}`;
};

const createTooltip = () =>
{
    popper.value = createPopperInstance(icon.value, menu.value, {
        strategy: 'fixed',
        placement: 'right',
        modifiers: [
            {
                name: 'flip',
                options: {
                    fallbackPlacements: ['top'],
                },
            }
        ],
    });
};

const destroyTooltip = () =>
{
    if (popper.value)
    {
        popper.value.destroy();
    }
};

const show = async (): Promise<void> =>
{
    createTooltip();
    menu.value.setAttribute('data-show', '');

    if (!currentVisits.value && props.isHistoryMode)
    {
        loadingVisits.value = true;
        currentVisits.value = (await ParticipantsService.getTargetVisits(props.licence, props.documentId, props.historyId, props.user.targetType, +props.user.user.key, true)).items.map(p => p.result);
        loadingVisits.value = false;
    }
};

const hide = (): void =>
{
    destroyTooltip();
    menu.value.removeAttribute('data-show');
};

const openReadSharingModal = async (): Promise<void> =>
{
    if (props.isHistoryMode || (props.user && props.user.roles && props.user.roles.length && props.user.roles.some(p => p.key == '5')))
    {
        if (props.isHistoryMode)
        {
            const allowedTargetTypes = [1, 3, 4, 5];

            if (!allowedTargetTypes.includes(props.user.targetType))
                return;

            if (props.user.targetType == 1)
                title.value = $t('[[[Pracownik]]]');

            if (props.user.targetType == 3)
                title.value = $t('[[[Dział]]]');

            if (props.user.targetType == 4)
                title.value = $t('[[[Zespół]]]');

            if (props.user.targetType == 5)
                title.value = $t('[[[Grupa użytkowników]]]');

            title.value += ": " + props.user.user.value;

            loadingModalVisits.value = true;
        }

        if (!props.isHistoryMode)
        {
            if (props.added || props.removed)
                return;

            if (!props.user.participantType || props.user.participantType.key == 'User')
                return;
        }

        readSharingModal.value?.show();

        if (props.isHistoryMode)
        {
            readSharingData.value = null;
            readSharingData.value = (await ParticipantsService.getTargetVisits(props.licence, props.documentId, props.historyId, props.user.targetType, +props.user.user.key, false)).items.map(p => ({
                user: p.result.seenBy,
                readSharingDateUtc: p.result.seenDateUtc
            }));

            loadingModalVisits.value = false;
        }
    }
};
</script>

<template>
    <div class="participant__item">
        <UserAvatar
            :src="$filters.baseurl(`storage/files/${user.user ? user.user.pictureUrl : user.user.pictureUrl}`)"
            :username="user.user.value || ''"
            rounded="circle"
            :class="customClass"
        />
        <div v-if="tooltip" class="participant__container" @mouseenter="show" @mouseleave="hide">
            <div ref="icon" class="participant__icon text-white" :class="added ? 'bg-success' : removed ? 'bg-danger' : 'bg-dark'" @click="openReadSharingModal">
                <i v-if="added" class="fa fa-check"></i>
                <i v-else-if="removed" class="fa fa-minus"></i>
                <template v-else>
                    <i v-if="isSomeoneHaveSeen || isUserWhoReadShare" class="fas fa-eye"></i>
                    <i v-else class="fas fa-info"></i>
                </template>
            </div>
            <Teleport to="body">
                <div ref="menu" class="participant__tooltip" @mouseenter="show" @mouseleave="hide">
                    <div class="text-start mb-0">
                        <strong class="d-block">{{ user.user ? user.user.value : '' }}</strong>
                        <template v-if="currentVisits">
                            <div v-for="seenBy in currentVisits" :key="seenBy.seenBy.key">
                                {{ messageSeenBy(seenBy) }}
                            </div>
                        </template>
                        <template v-if="loadingVisits">
                            <Loader class="mt-1" />
                        </template>
                        <template v-if="user && user.roles && user.roles.length">
                            <div v-for="(role, roleIndex) in user.roles" :key="roleIndex">
                                {{ $t('[[[Rola]]]') }}: {{ role.value }}
                                <template v-if="roleIndex + 1 < user.roles.length">, </template>
                            </div>
                        </template>
                        <div v-if="user && user.audit">
                            {{ $t('[[[Udostępnił]]]') }}: {{ user.audit.user.name }} {{ $t('[[[Data]]]') }}:
                            {{ $filters.datetime(user.audit.dateUtc) }}
                        </div>
                        <div v-if="isUserWhoReadShare">
                            {{ $t('[[[Zapoznał się]]]') }}: {{ $filters.datetime(user.readSharingData[0].readSharingDateUtc) }}
                        </div>
                        <slot />
                        <IdeoButton
                            v-if="canDelete"
                            :id="`btn-delete-${index}`"
                            class="my-1"
                            size="sm"
                            variant="danger"
                            :title="$t('[[[Odbierz uprawnienie]]]')"
                        >
                            {{ $t('[[[Odbierz uprawnienie]]]') }}
                        </IdeoButton>
                        <Confirmation
                            v-if="canDelete"
                            :message="$t('[[[Potwierdzenie usunięcia]]]')"
                            :target="`btn-delete-${index}`"
                            :placement="'right'"
                            @confirm="deleteItem(user)"
                        />
                    </div>
                </div>
            </Teleport>
        </div>
        <IdeoModal v-if="isHistoryMode" ref="readSharingModal" :title="title" centered size="lg">
            <template #default>
                <ListView :items="readSharingData" :preload="false" :loading="loadingModalVisits" class="read-sharing-container">
                    <template #row="{ item, index }">
                        <ListViewItem :title="$t('[[[Pracownik]]]')">
                            <div class="d-flex align-items-center">
                                <UserAvatar
                                    :key="(item.user as KeyValuePair).pictureUrl"
                                    :src="$filters.baseurl(`storage/files/${(item.user as KeyValuePair).pictureUrl}`)"
                                    :username="(item.user as KeyValuePair).value"
                                    class="cursor-pointer"
                                />
                                <p class="ms-2 mb-0">
                                    <strong class="text-primary cursor-pointer">
                                        <span>{{ (item.user as KeyValuePair).value }}</span>
                                    </strong>
                                </p>
                            </div>
                        </ListViewItem>
                        <ListViewItem :title="$t('[[[Data zapoznania]]]')">
                            {{ $filters.datetime(item.readSharingDateUtc as string) }}
                        </ListViewItem>
                    </template>
                </ListView>
            </template>
            <template #modal-footer="{ cancel }">
                <IdeoButton variant="secondary" @click="cancel()">
                    {{ $t('[[[Zamknij]]]') }}
                </IdeoButton>
            </template>
        </IdeoModal>
        <ReadSharingModal
            v-else
            ref="readSharingModal"
            :participant-guid="user.publicId"
            :licence="licence"
            :public-id="publicId"
            :title="title"
        />
    </div>
</template>

<style lang="scss" scoped>
.participant {
    &__item {
        position: relative;
        margin-right: 15px;
    }

    &__container {
        position: absolute;
        top: 50%;
        right: -6px;
        transform: translateY(-50%);
    }

    &__icon {
        width: 15px;
        height: 15px;
        border-radius: 50%;
        font-size: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    }

    &__tooltip {
        display: none;
        padding: 5px 10px;
        white-space: wrap;
        border-radius: 5px;
        background: var(--bs-black);
        opacity: 0.9;
        color: var(--bs-white);
        font-size: 11px;
        max-width: 270px;
        z-index: 9999;

        &[data-show] {
            display: block;
        }
    }
}
</style>
<style lang="scss">
.read-sharing-container{
    overflow-y: auto;
    max-height: 300px;
}
.participant__tooltip{
    .loader {
        border: 3px solid #f3f3f3 !important; /* Light grey */
        border-top: 3px solid #3498db !important; /* Blue */
        border-radius: 50% !important;
        width: 20px !important;
        height: 20px !important;
        animation: spin 2s linear infinite;
    }
}
</style>
